"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const getNutritionProgressForDayQuery_1 = __importDefault(require("./graphql/getNutritionProgressForDayQuery"));
const Range_1 = __importDefault(require("../../../Util/Range"));
const asyncIterate_1 = __importDefault(require("../../../Util/asyncIterate"));
const getSubscriptionWeekFromDate_1 = require("../../../Util/getSubscriptionWeekFromDate");
const calorieBudgetForSubscriptionWeeksQuery_1 = __importDefault(require("./graphql/calorieBudgetForSubscriptionWeeksQuery"));
const RangeUtils_1 = require("../../../Util/RangeUtils");
const fetchNutritionProgressByDateRange_1 = require("./graphql/fetchNutritionProgressByDateRange");
class Nutrition {
    constructor(client, dateTimeProvider, dateTimeModifier, customerClient, fetchRangeHelpers) {
        this.fetchDayTypeByRange = async (userId, range) => {
            const { progressCollection, } = await this.client.fetch("getProgressionDayTypeQuery", `query {progressCollection(range: {from: "${range.from}",to: "${range.to}"}, userId: ${userId}) {
            date
            dayType
      }}`, null);
            return progressCollection;
        };
        this.getNutritionProgressByDate = async (customerId, date) => {
            const customer = await this.customerClient.fetchCustomer(customerId);
            const result = await this.client.fetch("getNutritionProgressForDayQuery", getNutritionProgressForDayQuery_1.default, {
                userId: parseInt(customerId),
                date,
            });
            const progressCollection = result.progressCollection;
            const item = progressCollection[0] || null;
            if (item === null) {
                return null;
            }
            const daysTypes = await this.fetchDayTypeByRange(customerId, new Range_1.default(date, date));
            return await this.mapNutritionDay(customer, item, daysTypes[0].dayType, item.calorieBudget);
        };
        this.fetchNutritionProgressByDateRange = async (customerId, dateRange) => {
            // Need only method from customer
            const customer = await this.customerClient.fetchCustomer(customerId);
            const { progressCollection, } = await this.client.fetch("getNutritionProgressByRange", fetchNutritionProgressByDateRange_1.nutritionProgressByDateRangeQuery, {
                userId: parseInt(customerId),
                range: dateRange,
            });
            return {
                days: progressCollection
                    .map(this.addTrackMethodToDay(customer))
                    .map(this.parseDayOfWeek),
            };
        };
        this.addTrackMethodToDay = (customer) => (day) => {
            const { meals, ...rest } = day;
            return {
                ...rest,
                method: this.establishMethod(customer, day),
            };
        };
        this.parseDayOfWeek = (day) => ({
            ...day,
            day: this.dateTimeProvider.dayOfWeek(day.date),
        });
        this.mapNutritionDay = async (customer, progressItem, dayType, calorieBudget) => {
            const method = this.establishMethod(customer, progressItem);
            const meals = await (0, asyncIterate_1.default)(progressItem.meals, (meal) => this.mapDayMeal(meal));
            const day = {
                method: this.establishMethod(customer, progressItem),
                date: this.dateTimeProvider.fromDateTimeToDate(progressItem.date),
                meals: [],
                dayType,
                calorieBudgetInfo: progressItem.calorieBudgetInfo,
            };
            switch (method) {
                case "caloriecount":
                case "mixed":
                    return {
                        ...day,
                        meals: meals,
                        calorieBudget,
                        calorieBudgetCalories: (calorieBudget === null || calorieBudget === void 0 ? void 0 : calorieBudget.calories) || null,
                        nutritionFacts: this.sumMealNutritionFacts(meals.filter((meal) => meal.method === "caloriecount")),
                    };
                case "handpalm":
                    return {
                        ...day,
                        meals: meals,
                    };
            }
        };
        this.mapDayMeal = async (meal) => {
            const mealTemplate = {
                method: this.establishMealMethod(meal),
                name: meal.mealType,
                image: await this.mapImage(meal),
            };
            switch (mealTemplate.method) {
                case "caloriecount":
                    return {
                        ...mealTemplate,
                        isMealPlan: meal.isFromMenu,
                        nutritionFacts: meal.nutritionFacts,
                        nutrients: meal.nutrients.map(this.mapNutrient),
                    };
                case "handpalm":
                    return {
                        ...mealTemplate,
                        time: meal.time,
                        description: meal.description,
                    };
            }
        };
        this.sumMealNutritionFacts = (meals) => {
            // array.reduce
            const nutritionFacts = {
                calories: 0,
                carbs: 0.0,
                fats: 0.0,
                fibers: 0.0,
                protein: 0.0,
            };
            meals.forEach((meal) => {
                nutritionFacts.calories += meal.nutritionFacts.calories;
                nutritionFacts.carbs += meal.nutritionFacts.carbs;
                nutritionFacts.fats += meal.nutritionFacts.fats;
                nutritionFacts.fibers += meal.nutritionFacts.fibers;
                nutritionFacts.protein += meal.nutritionFacts.protein;
            });
            const fractionDigits = 2;
            // for in loop
            nutritionFacts.calories = parseFloat(nutritionFacts.calories.toFixed(fractionDigits));
            nutritionFacts.carbs = parseFloat(nutritionFacts.carbs.toFixed(fractionDigits));
            nutritionFacts.fats = parseFloat(nutritionFacts.fats.toFixed(fractionDigits));
            nutritionFacts.fibers = parseFloat(nutritionFacts.fibers.toFixed(fractionDigits));
            nutritionFacts.protein = parseFloat(nutritionFacts.protein.toFixed(fractionDigits));
            return nutritionFacts;
        };
        this.mapNutrient = (nutrient) => {
            return {
                name: nutrient.name,
                nutritionFacts: nutrient.nutritionFacts,
                measure: {
                    value: nutrient.measure ? nutrient.measure.value : 0,
                },
            };
        };
        this.establishMethod = (customer, { meals }) => {
            if (!meals || meals.length === 0) {
                return customer.method;
            }
            const firstMeal = meals[0];
            const method = this.establishMealMethod(firstMeal);
            if (meals.find((meal) => this.establishMealMethod(meal) !== method) !==
                undefined) {
                return "mixed";
            }
            return method;
        };
        this.mapImage = async (meal) => {
            if (!meal.imageWebPath || !meal.hasImage) {
                return null;
            }
            return {
                thumbnail: meal.imageWebPath, // do not authorize meal image, it is public
            };
        };
        this.establishMealMethod = (meal) => {
            return meal.isCalorieTracked ? "caloriecount" : "handpalm";
        };
        this.fetchCalorieBudgets = async (customerId, dateRange) => {
            const map = new Map();
            const ranges = [(0, RangeUtils_1.weekRangeFromDate)(dateRange.to)];
            if (!this.fetchRangeHelpers.isDateRangeInSameWeek(dateRange)) {
                ranges.push((0, RangeUtils_1.weekRangeFromDate)(dateRange.from));
            }
            const response = await this.client.fetch("calorieBudgetForSubscriptionWeeks", calorieBudgetForSubscriptionWeeksQuery_1.default, {
                id: parseInt(customerId),
            });
            ranges.forEach((range) => {
                const period = response.coachTrainee.periods.find(({ start, end }) => {
                    const a = this.dateTimeModifier.dateIsSameOrAfter(range.from, start);
                    const b = this.dateTimeModifier.dateIsSameOrAfter(end, range.to);
                    return a && b;
                });
                if (!period)
                    return;
                const week = (0, getSubscriptionWeekFromDate_1.getWeekNumberFromDateRangeByDate)(period.start, {
                    startDate: range.from,
                    endDate: range.to,
                });
                const { week: _, ...rest } = this.prepareCalorieBudgetBundleForWeek(period, week);
                map.set(`${range.from}~${range.to}`, rest);
            });
            return map;
        };
        this.prepareCalorieBudgetBundleForWeek = (period, week) => {
            const calorieBudgetBundle = {
                week,
                forNonTrainingDay: {
                    calories: 0,
                    carbohydrates: 0,
                    fats: 0,
                    proteins: 0,
                    fibers: 0,
                },
                forTrainingDay: {
                    calories: 0,
                    carbohydrates: 0,
                    fats: 0,
                    proteins: 0,
                    fibers: 0,
                },
            };
            if (period.calorieBudgets.length !== 0) {
                const budgets = this.pickBudgets(period.calorieBudgets, week);
                budgets.forEach((budget) => {
                    const { week, isForTrainingDays, ...nutritionFacts } = budget;
                    if (isForTrainingDays) {
                        calorieBudgetBundle.forTrainingDay = nutritionFacts;
                        return;
                    }
                    calorieBudgetBundle.forNonTrainingDay = nutritionFacts;
                });
            }
            return calorieBudgetBundle;
        };
        this.pickBudgets = (calorieBudgets, week) => {
            const budgetsUntilWeek = calorieBudgets.filter((calorieBudget) => calorieBudget.week <= week);
            const sortedBudgetsUntilWeek = budgetsUntilWeek.sort((budgetA, budgetB) => {
                if (budgetA.week > budgetB.week)
                    return -1;
                if (budgetA.week === budgetB.week)
                    return 0;
                return 1;
            });
            return [
                sortedBudgetsUntilWeek.find((budget) => budget.isForTrainingDays) ||
                    this.zeroValueCalorieBudget(week, true),
                sortedBudgetsUntilWeek.find((budget) => !budget.isForTrainingDays) ||
                    this.zeroValueCalorieBudget(week, false),
            ];
        };
        this.zeroValueCalorieBudget = (week, isForTrainingDays) => {
            return {
                week,
                calories: 0,
                carbohydrates: 0,
                fats: 0,
                fibers: 0,
                proteins: 0,
                isForTrainingDays: isForTrainingDays,
            };
        };
        this.client = client;
        this.dateTimeProvider = dateTimeProvider;
        this.dateTimeModifier = dateTimeModifier;
        this.customerClient = customerClient;
        this.fetchRangeHelpers = fetchRangeHelpers;
    }
}
exports.default = Nutrition;
//# sourceMappingURL=Nutrition.js.map